<template>
    <figure v-if="image" class="base-image__wrapper">
        <ImageSet
            v-slot="{ height, width, src, srcset, avifSrcset, webpSrcset }"
            :image="image"
        >
            <picture
                class="base-image"
                :class="[wrapperClass, { 'base-image--preserve-aspect-ratio': preserveAspectRatio }]"
            >
                <source
                    v-if="avifSrcset"
                    type="image/avif"
                    :sizes="sizes"
                    :srcset="avifSrcset"
                    :width="width"
                    :height="height"
                >
                <source
                    v-if="webpSrcset"
                    type="image/webp"
                    :sizes="sizes"
                    :srcset="webpSrcset"
                    :width="width"
                    :height="height"
                >
                <img
                    class="base-image__image"
                    :style="style"
                    :src="src"
                    :sizes="sizes"
                    :srcset="srcset"
                    :alt="imageAlt || ''"
                    :loading="loading"
                    :width="width"
                    :height="height"
                    :decoding="decoding"
                    :fetchpriority="fetchpriority"
                    v-bind="imageAttrs"
                >
            </picture>
        </ImageSet>

        <figcaption
            v-if="!hideCaption && (!!$slots.caption || image.caption || image.credits)"
            class="base-image__caption"
        >
            <slot name="caption">
                <span v-if="image.caption" v-html="image.caption" /> <!-- eslint-disable-line -->
                <span v-if="image.credits && image.caption"> - </span>
                <span v-if="image.credits" v-html="image.credits" /> <!-- eslint-disable-line -->
            </slot>
        </figcaption>
    </figure>
</template>

<script>
import ImageSet from '@/components/ImageSet/ImageSet.vue';

export default {
    components: {
        ImageSet
    },
    props: {
        image: {
            type: Object,
            required: true
        },
        sizes: {
            type: String,
            required: true,
            default: '(min-width: 768px) 1020px, 100vw'
        },
        loading: {
            type: String,
            default: 'lazy',
            validator: value => ['lazy', 'eager'].includes(value)
        },
        decoding: {
            type: String,
            default: 'auto',
            validator: value => ['sync', 'async', 'auto'].includes(value)
        },
        fetchpriority: {
            type: String,
            default: 'auto',
            validator: value => ['high', 'low', 'auto'].includes(value)
        },
        imageAlt: {
            type: String,
            default: ''
        },
        wrapperClass: {
            type: String,
            default: ''
        },
        imageAttrs: {
            type: Object,
            required: false,
            default: null
        },
        preserveAspectRatio: {
            type: Boolean,
            default: false
        },
        hideCaption: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        hasFocalPoint() {
            return this.image?.hasFocalPoint ?? false;
        },
        focalPoint() {
            return this.image?.focalPoint;
        },
        objectFitPositionX() {
            return (this.focalPoint?.[0] ?? 0) * 100;
        },
        objectFitPositionY() {
            return (this.focalPoint?.[1] ?? 0) * 100;
        },
        objectFitPosition() {
            return `${this.objectFitPositionX}% ${this.objectFitPositionY}%`;
        },
        style() {
            return {
                'object-position': this.hasFocalPoint ? this.objectFitPosition : null,
                'aspect-ratio': this.preserveAspectRatio ? `${this.image.width / this.image.height}` : 'initial'
            };
        }
    }
};
</script>

<style src="./BaseImage.less" lang="less" />
